/**
 * Client Outcome Tracking Page
 * Tracks and visualizes client health outcomes
 */

import { useState, useEffect } from 'react'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from '../components/Card'
import Button from '../components/Button'
import Input from '../components/Input'
import Select from '../components/Select'
import { LineChart, BarChart } from '../components/Chart'
import {
  Target,
  Plus,
  TrendingUp,
  TrendingDown,
  RefreshCw,
  Trash2,
} from 'lucide-react'
import {
  recordOutcome,
  getClientOutcomes,
  getClientOutcomeAnalytics,
  getOrganizationOutcomeStats,
} from '../services/clientOutcomeTracking'
import { formatDate } from '../utils/helpers'

const ClientOutcomes = () => {
  const organizationId = useOrganizationId()
  const [selectedClient, setSelectedClient] = useState(null)
  const [clients, setClients] = useState([])
  const [outcomes, setOutcomes] = useState([])
  const [analytics, setAnalytics] = useState(null)
  const [orgStats, setOrgStats] = useState(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [loading, setLoading] = useState(true)

  const [newOutcome, setNewOutcome] = useState({
    clientId: '',
    type: 'symptom_improvement',
    value: '',
    unit: '',
    baseline: '',
    notes: '',
  })

  useEffect(() => {
    if (organizationId) {
      loadClients()
      loadOrgStats()
    }
  }, [organizationId])

  useEffect(() => {
    if (selectedClient) {
      loadClientData()
    }
  }, [selectedClient, organizationId])

  const loadClients = async () => {
    try {
      const { supabase } = await import('../services/supabase')
      const { data, error } = await supabase
        .from('clients')
        .select('id, first_name, last_name')
        .eq('organization_id', organizationId)
        .order('last_name')

      if (!error && data) {
        setClients(data)
        if (data.length > 0 && !selectedClient) {
          setSelectedClient(data[0].id)
        }
      }
    } catch (error) {
      console.error('Error loading clients:', error)
    }
  }

  const loadClientData = async () => {
    if (!selectedClient) return

    setLoading(true)
    try {
      const [outcomesData, analyticsData] = await Promise.all([
        getClientOutcomes(organizationId, selectedClient),
        getClientOutcomeAnalytics(organizationId, selectedClient),
      ])

      if (outcomesData.success) {
        setOutcomes(outcomesData.data)
      }
      if (analyticsData.success) {
        setAnalytics(analyticsData.data)
      }
    } catch (error) {
      console.error('Error loading client data:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadOrgStats = async () => {
    try {
      const result = await getOrganizationOutcomeStats(organizationId, 30)
      if (result.success) {
        setOrgStats(result.data)
      }
    } catch (error) {
      console.error('Error loading org stats:', error)
    }
  }

  const handleCreateOutcome = async () => {
    if (!newOutcome.clientId || !newOutcome.value) {
      alert('Please fill in all required fields')
      return
    }

    const { supabase } = await import('../services/supabase')
    const {
      data: { user },
    } = await supabase.auth.getUser()

    const result = await recordOutcome(organizationId, newOutcome.clientId, {
      type: newOutcome.type,
      value: parseFloat(newOutcome.value),
      unit: newOutcome.unit,
      baseline: newOutcome.baseline ? parseFloat(newOutcome.baseline) : null,
      notes: newOutcome.notes,
      recordedBy: user?.id,
    })

    if (result.success) {
      setShowCreateModal(false)
      setNewOutcome({
        clientId: '',
        type: 'symptom_improvement',
        value: '',
        unit: '',
        baseline: '',
        notes: '',
      })
      loadClientData()
      loadOrgStats()
    } else {
      alert(`Error: ${result.error}`)
    }
  }

  const selectedClientName = clients.find((c) => c.id === selectedClient)

  return (
    <div className="section-container space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Target className="w-8 h-8" />
          Client Outcome Tracking
        </h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Record Outcome
        </Button>
      </div>

      {/* Organization Stats */}
      {orgStats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <p className="text-sm text-gray-600">Total Outcomes</p>
            <p className="text-3xl font-bold">{orgStats.totalOutcomes}</p>
          </Card>
          <Card>
            <p className="text-sm text-gray-600">Unique Clients</p>
            <p className="text-3xl font-bold">{orgStats.uniqueClients}</p>
          </Card>
          <Card>
            <p className="text-sm text-gray-600">Avg Improvement</p>
            <p className="text-3xl font-bold">{orgStats.averageImprovementRate}%</p>
          </Card>
          <Card>
            <p className="text-sm text-gray-600">Outcome Types</p>
            <p className="text-3xl font-bold">{Object.keys(orgStats.outcomeTypes).length}</p>
          </Card>
        </div>
      )}

      {/* Client Selection */}
      <Card>
        <label className="block text-sm font-medium mb-2">Select Client</label>
        <Select
          value={selectedClient || ''}
          onChange={(e) => setSelectedClient(e.target.value)}
          options={clients.map((c) => ({
            value: c.id,
            label: `${c.first_name} ${c.last_name}`,
          }))}
        />
      </Card>

      {selectedClient && (
        <>
          {/* Client Analytics */}
          {analytics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <h2 className="text-xl font-semibold mb-4">
                  {selectedClientName?.first_name} {selectedClientName?.last_name} - Outcome
                  Analytics
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Outcomes</span>
                    <span className="font-semibold">{analytics.totalOutcomes}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Improvement Rate</span>
                    <span
                      className={`font-semibold ${
                        analytics.improvementRate >= 70
                          ? 'text-green-600'
                          : analytics.improvementRate >= 50
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}
                    >
                      {analytics.improvementRate}%
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2">Outcomes by Type</p>
                    <div className="space-y-1">
                      {Object.entries(analytics.outcomeTypes).map(([type, count]) => (
                        <div key={type} className="flex justify-between text-sm">
                          <span className="capitalize">{type.replace(/_/g, ' ')}</span>
                          <span className="font-semibold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Outcome Trends Chart */}
              {analytics.trends && Object.keys(analytics.trends).length > 0 && (
                <Card>
                  <h2 className="text-xl font-semibold mb-4">Outcome Trends</h2>
                  {Object.entries(analytics.trends).map(([type, trendData]) => (
                    <div key={type} className="mb-4">
                      <h3 className="text-sm font-medium mb-2 capitalize">
                        {type.replace(/_/g, ' ')}
                      </h3>
                      <LineChart
                        data={trendData.map((t) => ({
                          date: new Date(t.date).toLocaleDateString(),
                          value: t.value,
                        }))}
                        xKey="date"
                        yKey="value"
                        height={150}
                      />
                    </div>
                  ))}
                </Card>
              )}
            </div>
          )}

          {/* Outcomes List */}
          <Card>
            <h2 className="text-xl font-semibold mb-4">Outcome History</h2>
            {loading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2 text-primary-600 dark:text-primary-400" />
                <p className="text-gray-600 dark:text-gray-400">Loading client data...</p>
              </div>
            ) : outcomes.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No outcomes recorded</p>
            ) : (
              <div className="space-y-2">
                {outcomes.map((outcome) => {
                  const improvement =
                    outcome.baseline_value && outcome.outcome_value
                      ? ((outcome.outcome_value - outcome.baseline_value) / outcome.baseline_value) *
                        100
                      : null

                  return (
                    <div
                      key={outcome.id}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded"
                    >
                      <div className="flex-1">
                        <p className="font-medium capitalize">
                          {outcome.outcome_type.replace(/_/g, ' ')}
                        </p>
                        <p className="text-sm text-gray-600">
                          {outcome.outcome_value} {outcome.outcome_unit || ''} •{' '}
                          {formatDate(outcome.measurement_date)}
                        </p>
                        {outcome.baseline_value && (
                          <p className="text-xs text-gray-500">
                            Baseline: {outcome.baseline_value} {outcome.outcome_unit || ''}
                          </p>
                        )}
                        {improvement !== null && (
                          <div className="flex items-center gap-2 mt-1">
                            {improvement > 0 ? (
                              <TrendingUp className="w-4 h-4 text-green-500" />
                            ) : (
                              <TrendingDown className="w-4 h-4 text-red-500" />
                            )}
                            <span
                              className={`text-xs font-semibold ${
                                improvement > 0 ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {improvement > 0 ? '+' : ''}
                              {improvement.toFixed(1)}%
                            </span>
                          </div>
                        )}
                        {outcome.notes && (
                          <p className="text-sm text-gray-500 mt-1">{outcome.notes}</p>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Record Outcome</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Client</label>
                <Select
                  value={newOutcome.clientId}
                  onChange={(e) => setNewOutcome({ ...newOutcome, clientId: e.target.value })}
                  options={clients.map((c) => ({
                    value: c.id,
                    label: `${c.first_name} ${c.last_name}`,
                  }))}
                  placeholder="Select client"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Outcome Type</label>
                <Select
                  value={newOutcome.type}
                  onChange={(e) => setNewOutcome({ ...newOutcome, type: e.target.value })}
                  options={[
                    { value: 'symptom_improvement', label: 'Symptom Improvement' },
                    { value: 'goal_achievement', label: 'Goal Achievement' },
                    { value: 'metric_change', label: 'Metric Change' },
                    { value: 'quality_of_life', label: 'Quality of Life' },
                    { value: 'other', label: 'Other' },
                  ]}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Value</label>
                  <Input
                    type="number"
                    value={newOutcome.value}
                    onChange={(e) => setNewOutcome({ ...newOutcome, value: e.target.value })}
                    placeholder="Value"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Unit</label>
                  <Input
                    value={newOutcome.unit}
                    onChange={(e) => setNewOutcome({ ...newOutcome, unit: e.target.value })}
                    placeholder="Unit (optional)"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Baseline Value (optional)</label>
                <Input
                  type="number"
                  value={newOutcome.baseline}
                  onChange={(e) => setNewOutcome({ ...newOutcome, baseline: e.target.value })}
                  placeholder="Baseline value"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Notes</label>
                <textarea
                  value={newOutcome.notes}
                  onChange={(e) => setNewOutcome({ ...newOutcome, notes: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={3}
                />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleCreateOutcome} className="flex-1">
                  Record
                </Button>
                <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}

export default ClientOutcomes

